import type { OrderTotalsInput, OrderTotals, ValidatedCartItem } from "./types"
import { SELECTION_UNIT_KG } from "@/constants/misc"

export function getEffectivePrice(item: ValidatedCartItem): number {
  if (item.unit === SELECTION_UNIT_KG && item.pricePerKg != null) return item.pricePerKg
  if (item.pricePerPiece != null) return item.pricePerPiece
  return item.price
}

export function calculateOrderTotals(input: OrderTotalsInput): OrderTotals {
  const { items, fees, accountBalance = 0, couponDiscount = 0, deliveryFeeOverride, expressDelivery = false, expressDeliveryFeeOverride } = input

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0)

  // Delivery fee — use server override when available (post-coupon), otherwise threshold-based
  const deliveryFee =
    deliveryFeeOverride !== undefined
      ? deliveryFeeOverride
      : fees.freeDeliveryThreshold !== null && subtotal >= fees.freeDeliveryThreshold
        ? 0
        : fees.deliveryFee

  // Service fee — flat or percentage, whichever is configured
  const serviceFee =
    fees.freeServiceThreshold !== null && subtotal >= fees.freeServiceThreshold
      ? 0
      : fees.serviceFee

  const expressDeliveryFee = expressDeliveryFeeOverride ?? (expressDelivery ? fees.expressDeliveryFee : 0)

  // Coupon capped at subtotal
  const appliedCoupon = Math.min(couponDiscount, subtotal)

  const preBalanceTotal = subtotal + deliveryFee + serviceFee + expressDeliveryFee - appliedCoupon
  const balanceApplied = Math.min(accountBalance, Math.max(preBalanceTotal, 0))
  const total = Math.max(preBalanceTotal - balanceApplied, 0)

  const totalUserNeedsToPay = subtotal + deliveryFee + serviceFee - balanceApplied

  return {
    subtotal,
    deliveryFee,
    serviceFee,
    expressDeliveryFee,
    couponDiscount: appliedCoupon,
    balanceApplied,
    total,
    totalUserNeedsToPay,
  }
}
